import React, { useState } from "react";
import { FileText, FileSpreadsheet, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DocumentArtifact, downloadArtifact } from "@/lib/documentConverter";
import { useToast } from "@/hooks/use-toast";

interface ChatArtifactProps {
  artifact: DocumentArtifact;
}

export const ChatArtifact: React.FC<ChatArtifactProps> = ({ artifact }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { toast } = useToast();

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await downloadArtifact(artifact);
      toast({
        title: "Download started",
        description: `${artifact.filename} is being downloaded`,
      });
    } catch (error: any) {
      toast({
        title: "Download failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const getIcon = () => {
    switch (artifact.type) {
      case 'xlsx':
      case 'csv':
        return <FileSpreadsheet className="h-5 w-5 text-green-500" />;
      default:
        return <FileText className="h-5 w-5 text-blue-500" />;
    }
  };

  const getTypeLabel = () => {
    switch (artifact.type) {
      case 'docx':
        return 'Word Document';
      case 'pdf':
        return 'PDF Document';
      case 'xlsx':
        return 'Excel Spreadsheet';
      case 'csv':
        return 'CSV File';
      default:
        return 'Document';
    }
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-background border border-border rounded-lg mt-2 hover:border-primary/50 transition-colors">
      <div className="relative flex-shrink-0">
        {getIcon()}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleDownload}
          disabled={isDownloading}
          className="absolute -bottom-1 -right-1 h-4 w-4 p-0 bg-background border border-border rounded-full hover:bg-muted"
        >
          {isDownloading ? (
            <Loader2 className="h-2.5 w-2.5 animate-spin" />
          ) : (
            <Download className="h-2.5 w-2.5" />
          )}
        </Button>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{artifact.filename}</p>
        <p className="text-xs text-muted-foreground">{getTypeLabel()}</p>
      </div>
    </div>
  );
};
